import {ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import * as echarts from 'echarts';
import type {EChartsOption} from 'echarts';
import * as worldMap from "@highcharts/map-collection/custom/world.geo.json";
import {
  NetscopeApiService
} from "@app/services/netscope-api.service";
import {ClrDatagridSortOrder} from "@clr/angular";
import {TranslocoService} from "@ngneat/transloco";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {combineLatest, Subscription} from "rxjs";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {
  ComboboxSelectionItem,
  FlowDetail, FlowRessourceObject,
  TemporalParameter
} from "@app/netscope/model/data-models";
import {FlowsRepository, OutsideFlowsComponentData} from "@app/netscope/model/flows-repository";

enum SelectedModalValues {
  VMS_DETAILS = "vmsDetails",
  COUNTRIES_DETAILS = "countriesDetails",
  COUNTRY_CODES_DETAILS = "countriesCodeDetails",
  ASNS_DETAILS = "asNDetails",
  OPEN_PORTS_DETAILS = "openPortsDetails",
}

export class VmWithPortsAccessedFromOutsideInfo {
  name: string;
  uuid: string;
  ports_count: number;
  ips_count: number;
  unique_ips: string[];

  public constructor(uuid: string, name: string, ports_count: number, unique_ips: string[]) {
    this.uuid = uuid;
    this.name = name;
    this.ports_count = ports_count;
    this.unique_ips = unique_ips;
    this.ips_count = unique_ips.length;
  }
}

export class CountryFlow {
  country: string;
  countryCode: string;
  exchanged_bytes: number;
  exchanged_packets: number;
  count: number;
}

export class AsnFlow {
  exchanged_bytes: number;
  exchanged_packets: number;
  network: string;
  asnNum: number
  count: number;
}

// World map of the Highcharts map collection, registered for ECharts. Its coordinates are already projected (Miller
// projection, north up), so ECharts draws them as they are (aspectScale 1); its countries are named by their lowercase
// ISO code ('hc-key'), which is also the key of the countries ranking.
const WORLD_MAP_NAME = "netscope-world";
// Room kept above and under the map, the legend being under it (pixels)
const MAP_TOP = 10;
const MAP_BOTTOM = 60;
// Factor of a click on the zoom buttons of the map
const MAP_ZOOM_STEP = 1.5;
const WORLD_MAP_GEOJSON: any = (worldMap as any).default ?? worldMap;
echarts.registerMap(WORLD_MAP_NAME, WORLD_MAP_GEOJSON);

// Names of the countries, by the key the map knows them by
const COUNTRY_NAMES: Map<string, string> = new Map(
  WORLD_MAP_GEOJSON.features.map((feature) => [feature.properties["hc-key"], feature.properties.name])
);

export class ResourceFlow {
  exchanged_bytes: number;
  exchanged_packets: number;
  resourceUuid: string;
  count: number;
  countPort?: number;
  countIpaddress?: number;
  resource: FlowRessourceObject;
}

@Component({
  standalone: false,
  selector: 'app-netscope-outside-flows',
  templateUrl: './netscope-outside-flows.component.html',
  styleUrls: ['./netscope-outside-flows.component.css']
})
export class NetscopeOutsideFlowsComponent implements OnInit, OnDestroy {

  @Output()
  loadingStateEmitter = new EventEmitter<boolean>();
  @Output()
  errorStateEmitter = new EventEmitter<boolean>();

  // Number of flows per country (lowercase ISO code), shown by the world map
  countriesRanking: Map<string, number> = new Map();
  countriesMapOptions: EChartsOption = {};
  // ECharts instance of the world map, zoomed by its buttons
  private countriesMap: any;
  private darkModeSubscription: Subscription;

  showFlowModal = false;
  selectFlowDetailsModalTitle = "";
  selectedModal: string | undefined;
  selectedModalValue: string | undefined;
  selectFlowDetails: FlowDetail[] = [];

  selectedFlowsRepository: FlowsRepository;
  outsideFlowsComponentData: OutsideFlowsComponentData;

  vmsWithFlowsOutside: ResourceFlow[] = [];
  countriesWithFlowsOutside: CountryFlow[] = [];
  networksWithFlowsOutside: AsnFlow[] = [];
  vmsWithPortsAccessedFromOutside: ResourceFlow[] = [];
  vmsCountWithManyOpenPorts = 0;
  vmsCountWithFewOpenPorts = 0;
  vmsCountWithManyIpConnections = 0;
  vmsCountWithFewIpConnections = 0;

  temporalParameter: TemporalParameter;

  shouldUpdateFilters: EventEmitter < any > = new EventEmitter();

  modalOptions = {
    dontGroupIps: false
  };

  failureMode = false;
  isLoading = false;

  outsideFlowsTraffic = 0;
  vmsCountWithOutsideFlows = 0;
  outsideFlowsCount = 0;
  outsideFlows: FlowDetail[] = [];
  outsideFlowsOpenPorts: FlowDetail[] = [];

  overviewSelection = "vms";

  descSort = ClrDatagridSortOrder.DESC;
  clustersFlows: FlowDetail[] = [];
  flowsRepository: FlowsRepository;
  currentComboboxSelection: ComboboxSelectionItem[] = [];

  constructor(public netscopeService: NetscopeApiService, public translocoService: TranslocoService, private netscopeUiService: NetscopeUiService, private ref: ChangeDetectorRef,
              private host: ElementRef) { }

  ngOnInit(): void {
    // The theme class of the application changes along with the dark mode: the map is drawn again once it did
    this.darkModeSubscription = this.netscopeUiService.darkModeStatusSubject.subscribe(() => {
      setTimeout(() => this.renderCountriesMap());
    });

    this.netscopeUiService.temporalParametersSubject.subscribe((newTemporalParameters) => {
      this.temporalParameter = newTemporalParameters;
    })

    combineLatest([
      this.netscopeUiService.comboboxSelectionItemSubject,
      this.netscopeUiService.clustersFlowDetailsSubject,
      this.netscopeUiService.duckDbRefinedDataSubject
    ])
      .subscribe(async ([comboboxSelectionItems, clustersFlows, duckDbData]) => {
        this.flowsRepository = duckDbData.flowsRepository.clone();
        await this.flowsRepository.setWhereClause("where src_resource_type == 'internet_ip' or dst_resource_type == 'internet_ip' ");
        // this.flowsRepository.setWhereClause(this.flowsRepository.whereClause);

        this.selectedFlowsRepository = this.flowsRepository.clone();

        let outsideFlowsComponentData = await this.flowsRepository.getOutsideFlowsComponentData();
        this.outsideFlowsComponentData = outsideFlowsComponentData;

        if (! this.flowsRepository.firstPageShown) {
          this.flowsRepository.showFirstPage();
        }
        // this.askRefreshAngular();
        this.shouldUpdateFilters.emit();

        this.clustersFlows = clustersFlows.flows;
        this.currentComboboxSelection = comboboxSelectionItems;
        this.reloadUi();
      });
  }

  ngOnDestroy(): void {
    this.darkModeSubscription?.unsubscribe();
  }

  reloadData = () => {
    this.isLoading = true;
    this.loadingStateEmitter.emit(true);
  }

  reloadUi = () => {
    console.log("[outside flows] reload UI");

    this.outsideFlowsTraffic = this.outsideFlowsComponentData.outside.amountOfDataProcessed;
    this.vmsCountWithOutsideFlows = this.outsideFlowsComponentData.resourceCount;
    this.outsideFlowsCount = this.outsideFlowsComponentData.outside.amountOfFlowsProcessed;

    this.vmsCountWithManyIpConnections = this.outsideFlowsComponentData.resourceAccessibleFromOutsideRanking.size;

    // Ranking countries
    let countriesRanking = new Map<string, number>();

    for (let countryCode of NetscopeUtils.allCountriesCodes) {
      countriesRanking.set(countryCode, 0);
    }

    for (let countryFlow of this.outsideFlowsComponentData.countryRanking.values()) {
      let countryMapKey = countryFlow.countryCode.toLowerCase();
      countriesRanking.set(countryMapKey, countryFlow.count);
    }

    this.countriesWithFlowsOutside = Array.from(this.outsideFlowsComponentData.countryRanking.values());

    // Ranking asn
    this.networksWithFlowsOutside = Array.from(this.outsideFlowsComponentData.asnRanking.values());

    // Ranking resources
    this.vmsWithFlowsOutside = Array.from(this.outsideFlowsComponentData.resourceRanking.values());

    // Ranking vms accesssible from outside
    this.vmsWithPortsAccessedFromOutside = Array.from(this.outsideFlowsComponentData.resourceAccessibleFromOutsideRanking.values());

    this.countriesRanking = countriesRanking;
    this.renderCountriesMap();

    // Ask to update filters
    this.shouldUpdateFilters.emit(true);
    this.askRefreshAngular();
  }

  // World map of the countries the flows go to or come from, colored by their number of flows
  renderCountriesMap = () => {
    let style = getComputedStyle(this.host.nativeElement);
    let textColor = style.getPropertyValue('--highcharts-neutral-color-80').trim() || '#333333';
    let maxCount = Math.max(1, ...this.countriesRanking.values());

    this.countriesMapOptions = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          let count = typeof params.value === 'number' ? params.value : 0;
          return `${COUNTRY_NAMES.get(params.name) ?? params.name}: <b>${count.toLocaleString()}</b> connections`;
        }
      },
      // Horizontal legend of the colors, centered under the map
      visualMap: {
        min: 0,
        max: maxCount,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        inRange: {color: ['#e4e8ff', '#0122ff']},
        textStyle: {color: textColor},
        // The colors stand for numbers of connections
        formatter: (value: number) => `${Math.round(value).toLocaleString()} connections`
      },
      series: [{
        type: 'map',
        map: WORLD_MAP_NAME,
        nameProperty: 'hc-key',
        aspectScale: 1,
        // Room under the map for the legend
        top: MAP_TOP,
        bottom: MAP_BOTTOM,
        // Zoom with the mouse wheel, move by dragging
        roam: true,
        cursor: 'pointer',
        itemStyle: {borderColor: 'rgba(0, 0, 0, 0.5)', borderWidth: 0.4},
        // Names of the countries; those that would overlap others are hidden, and show up when zooming in
        label: {
          show: true,
          fontSize: 12,
          color: textColor,
          formatter: (params: any) => COUNTRY_NAMES.get(params.name) ?? params.name
        },
        labelLayout: {hideOverlap: true},
        emphasis: {
          itemStyle: {areaColor: '#BADA55'},
          label: {
            show: true,
            color: '#000000',
            formatter: (params: any) => COUNTRY_NAMES.get(params.name) ?? params.name
          }
        },
        selectedMode: false,
        data: [...this.countriesRanking.entries()].map(([countryCode, count]) => ({name: countryCode, value: count}))
      }]
    };
  }

  onCountriesMapInit = (chart) => {
    this.countriesMap = chart;
  }

  // Zoom buttons of the map: zoom in (factor above 1) or out (below 1) around the middle of the map
  zoomCountriesMap = (factor: number) => {
    if (this.countriesMap === undefined) {
      return;
    }
    this.countriesMap.dispatchAction({
      type: 'geoRoam',
      componentType: 'series',
      seriesIndex: 0,
      zoom: factor,
      originX: this.countriesMap.getWidth() / 2,
      originY: MAP_TOP + (this.countriesMap.getHeight() - MAP_TOP - MAP_BOTTOM) / 2
    });
  }

  // Back to the whole world: the options are replaced, which resets the zoom and the moves
  resetCountriesMapZoom = () => {
    this.renderCountriesMap();
  }

  protected readonly MAP_ZOOM_STEP = MAP_ZOOM_STEP;

  onCountriesMapClick = (event) => {
    if (event.componentType === 'series' && event.name) {
      this.filterCountryCodeAndShowModal(event.name);
    }
  }

  askRefreshAngular = () => {
    this.ref.detectChanges();
  }

  filterVmsAndShowModal = (vmUuid: string) => {
    let label = this.translocoService.translate("dcnetscope.supervision.outside_flows.flows_with_outside_ips_label");
    this.selectedModal = SelectedModalValues.VMS_DETAILS;
    this.selectedModalValue = vmUuid;

    this.selectedFlowsRepository = this.flowsRepository.clone();
    this.selectedFlowsRepository.setWhereClause(` (src_resource_uuid = '${vmUuid}' and dst_resource_type = 'internet_ip') or (dst_resource_uuid = '${vmUuid}' and src_resource_type = 'internet_ip') `);

    this.selectFlowDetailsModalTitle = label;
    this.showFlowModal = true;
  }

  filterCountryAndShowModal = (country: string, countryCode: string) => {
    let label = this.translocoService.translate("dcnetscope.supervision.outside_flows.flows_with_label", { variable: country });
    this.selectedModal = SelectedModalValues.COUNTRIES_DETAILS;
    this.selectedModalValue = country;

    this.selectedFlowsRepository = this.flowsRepository.clone();
    this.selectedFlowsRepository.setWhereClause(` src_country_code = '${countryCode}' or dst_country_code = '${countryCode}' `);

    this.selectFlowDetailsModalTitle = label;
    this.showFlowModal = true;
  }

  filterCountryCodeAndShowModal = (countryCode: string) => {
    let label = this.translocoService.translate("dcnetscope.supervision.outside_flows.flows_with_label", { variable: countryCode });
    this.selectedModal = SelectedModalValues.COUNTRY_CODES_DETAILS;
    this.selectedModalValue = countryCode;

    let countryCodeUpperCased = countryCode.toUpperCase();

    this.selectedFlowsRepository = this.flowsRepository.clone();
    this.selectedFlowsRepository.setWhereClause(` src_country_code = '${countryCodeUpperCased}' or dst_country_code = '${countryCodeUpperCased}' `);

    this.selectFlowDetailsModalTitle = label;
    this.showFlowModal = true;
  }

  filterNetworkProviderAndShowModal = (networkProvider: string, asnNumber: number) => {
    let label = this.translocoService.translate("dcnetscope.supervision.outside_flows.flows_with_label", { variable: networkProvider });
    this.selectedModal = SelectedModalValues.ASNS_DETAILS;
    this.selectedModalValue = networkProvider;

    this.selectedFlowsRepository = this.flowsRepository.clone();
    this.selectedFlowsRepository.setWhereClause(` src_asn_num = ${asnNumber} or dst_asn_num = '${asnNumber}' `);

    this.selectFlowDetailsModalTitle = label;
    this.showFlowModal = true;
  }

  filterVmsWithOpenPortsAndShowModal = (vmUuid: string) => {
    let label = this.translocoService.translate("dcnetscope.supervision.outside_flows.flows_with_outside_ips_label");
    this.selectedModal = SelectedModalValues.OPEN_PORTS_DETAILS;
    this.selectedModalValue = vmUuid;

    this.selectedFlowsRepository = this.flowsRepository.clone();
    this.selectedFlowsRepository.aggregated_flows_files = this.selectedFlowsRepository.flows_files;
    this.selectedFlowsRepository.setWhereClause(`
      ((src_resource_uuid = '${vmUuid}') and (dst_resource_type = 'internet_ip' and direction = 1))

      or

      ((dst_resource_uuid = '${vmUuid}') and (src_resource_type = 'internet_ip' and direction = 0))
    `);

    this.selectFlowDetailsModalTitle = label;
    this.showFlowModal = true;
  }

  exportCSV = () => {
    let csvHeader = "Source, Destination, SourceAddress, DestinationAddress, SourceCountry, DestinationCountry, SourceNetwork, DestinationNetwork, ExchangedBytes, TotalPackets"
    let csvContent = csvHeader + "\n";
    for (let link of this.outsideFlows) {
      let sourceName = link.source.name;
      let targetName = link.target.name;

      let sourceAsn = link.source.type == "internet_ip" ? "this infrastructure" : link.source.asnInfo?.asn.replaceAll(",", "_");
      let targetAsn = link.target.type == "internet_ip" ? "this infrastructure" : link.target.asnInfo?.asn.replaceAll(",", "_");

      let sourceCountry = link.source.countryInfo.country == undefined ? "this infrastructure" : link.source.countryInfo;
      let targetcountry = link.target.countryInfo.country == undefined ? "this infrastructure" : link.target.countryInfo;

      let lineValue =
          `${sourceName}, ${targetName}, ${link.source.address}, ${link.target.address}, ${sourceCountry}, ${targetcountry}, ${sourceAsn}, ${targetAsn}, ${link.exchanged_bytes}, ${link.exchanged_packets}\n`;
      csvContent += lineValue;
    }

    let exportedFilename = 'netscope-external-flows.csv';
    let blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    // @ts-ignore
    if (navigator.msSaveBlob) { // IE 10+
      // @ts-ignore
      navigator.msSaveBlob(blob, exportedFilename);
    } else {
      let link = document.createElement("a");
      if (link.download != undefined) { // feature detection
        // Browsers that support HTML5 download attribute
        let url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", exportedFilename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  protected readonly NetscopeUtils = NetscopeUtils;
  protected readonly SelectedModalValues = SelectedModalValues;
}
